import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { blogService } from '../../services';
import { buildCategoryTree } from '../../utils/blog';
import { useNotification } from '../../contexts/NotificationContext';
import RichTextEditor from '../../components/blog/RichTextEditor';
import { LoadingSpinner } from '../../components/ui/Loading';
import PageContainer from '../../components/PageContainer';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const AdminBlogForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { notify } = useNotification();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    title: '',
    categoryId: '',
    status: 'DRAFT',
    visibility: 'INTERNAL',
    body: '',
  });

  useEffect(() => {
    blogService.getCategories()
      .then((res) => setCategories(res.categories || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    blogService.getPost(id)
      .then((post) => {
        if (!active) return;
        setForm({
          title: post.title || '',
          categoryId: post.categoryId || '',
          status: post.status || 'DRAFT',
          visibility: post.visibility || 'INTERNAL',
          body: post.body || '',
        });
      })
      .catch(() => notify.error('Erro ao carregar o post', 'Erro'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, isEdit, notify]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Título é obrigatório';
    if (!form.categoryId) e.categoryId = 'Categoria é obrigatória';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setSaving(true);
      if (isEdit) {
        await blogService.updatePost(id, form);
        notify.success('Post atualizado!', '✅ Blog');
      } else {
        await blogService.createPost(form);
        notify.success('Post criado!', '✅ Blog');
      }
      navigate('/app/admin/blog');
    } catch (e) {
      notify.error(e.response?.data?.error?.title || 'Erro ao salvar o post', 'Erro');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer title={isEdit ? 'Editar Post' : 'Novo Post'} description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  const tree = buildCategoryTree(categories);

  return (
    <PageContainer
      title={isEdit ? 'Editar Post' : 'Novo Post'}
      description="Publique e edite os posts do clã"
    >
      <Link to="/app/admin/blog" className="text-sm text-purple-300 hover:underline">← Voltar</Link>

      <Card variant="glass" className="mt-4">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                className={`w-full px-4 py-2 bg-slate-700/50 border rounded-lg text-white ${errors.title ? 'border-red-500/50' : 'border-white/20'}`}
              />
              {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Categoria *</label>
                <select
                  value={form.categoryId}
                  onChange={(e) => setField('categoryId', e.target.value)}
                  className={`w-full px-4 py-2 bg-slate-700/50 border rounded-lg text-white ${errors.categoryId ? 'border-red-500/50' : 'border-white/20'}`}
                >
                  <option value="">Selecione...</option>
                  {tree.map((parent) => (
                    <optgroup key={parent.id} label={parent.name}>
                      <option value={parent.id}>{parent.name}</option>
                      {parent.children.map((child) => (
                        <option key={child.id} value={child.id}>&nbsp;&nbsp;{child.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {errors.categoryId && <p className="mt-1 text-sm text-red-400">{errors.categoryId}</p>}
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Status</label>
                <select value={form.status} onChange={(e) => setField('status', e.target.value)} className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white">
                  <option value="DRAFT">Rascunho</option>
                  <option value="PUBLISHED">Publicado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Visibilidade</label>
                <select value={form.visibility} onChange={(e) => setField('visibility', e.target.value)} className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white">
                  <option value="ADMIN_ONLY">Somente admin</option>
                  <option value="INTERNAL">Interno (logados)</option>
                  <option value="PUBLIC">Público</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Conteúdo (HTML)</label>
              <RichTextEditor value={form.body ? form.body : '<iframe\n' +
                '  frameborder="0" width="100%" height="800px"\n' +
                '  src="LINK">\n' +
                '</iframe>'} onChange={(html) => setField('body', html)} />
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="secondary" fullWidth onClick={() => navigate('/app/admin/blog')}>
                Cancelar
              </Button>
              <Button type="submit" fullWidth loading={saving} disabled={saving}>
                {isEdit ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </PageContainer>
  );
};

export default AdminBlogForm;
